// Shared TypeScript types — kept narrow on purpose. The backend response
// shapes are documented in services/api/app/cameras/schemas.py and
// app/auth/schemas.py.

export type Role = "admin" | "operator" | "viewer" | "auditor";

export type CameraStatus =
  | "online"
  | "offline"
  | "error"
  | "pending"
  | "unprovisioned";

export type ConnectionMode =
  | "agent"        // Sprint 4 — pushed via local slink-agent
  | "direct"       // Sprint 4 — pushed directly to cloud edge
  | "direct_rtsp"  // Sprint 10 — admin-managed, cloud mediamtx pulls from rtsp_url
  | "slink_agent"  // Sprint 10 — placeholder, future
  | "onpremise";   // Sprint 10 — placeholder, future

export interface User {
  id: string;
  tenant_id: string;
  email: string;
  full_name: string | null;
  role: Role;
  is_active: boolean;
  is_mfa_enabled: boolean;
  must_change_password: boolean;
  last_login_at?: string | null;
  // P5 — effective S4 permission keys from /auth/me (legacy-role perms
  // unioned with custom-role grants). The nav derives visibility from
  // these. Compare against the S4 key, never the legacy enum name
  // (e.g. "playback"/"export", not "access_recordings"/"export_recordings").
  permissions: string[];
}

export interface LoginResponse {
  access_token?: string | null;
  refresh_token?: string | null;
  token_type: string;
  expires_in?: number | null;
  requires_mfa: boolean;
  mfa_setup_required: boolean;
  must_change_password: boolean;
  mfa_token?: string | null;
}

export interface Camera {
  id: string;
  tenant_id: string;
  site_id: string;
  agent_id: string | null;
  registration_code: string;
  name: string;
  manufacturer?: string | null;
  model?: string | null;
  firmware?: string | null;
  serial_number?: string | null;
  onvif_host?: string | null;
  onvif_port?: number | null;
  onvif_profile_token?: string | null;
  onvif_username?: string | null;
  onvif_password?: string | null;
  rtsp_main_url?: string | null;
  rtsp_sub_url?: string | null;
  rtsp_username?: string | null;
  rtsp_password?: string | null;
  is_ptz: boolean;
  has_audio: boolean;
  resolution_width?: number | null;
  resolution_height?: number | null;
  framerate?: number | null;
  status: CameraStatus;
  connection_mode: ConnectionMode;
  hls_url?: string | null;
  last_status_change?: string | null;
  last_seen_at?: string | null;
  created_at: string;
  updated_at: string;
  // Sprint 10 — admin-managed cameras carry these; legacy rows leave
  // them undefined / null. Optional so existing call sites keep
  // compiling.
  stream_key?: string | null;
  is_active?: boolean;
  audio_enabled?: boolean;
  extra_metadata?: Record<string, unknown>;
  // Batch 1 (UX/IA) — hierarchy context the API already returns but the FE
  // previously dropped. Surfaced via <DeviceContextLabel> (Site › NVR › Ch)
  // and used to group the camera list by NVR. `site_name` and (dormant)
  // `source_id` are added by the Batch-1 read-path; the rest are the
  // denormalized NVR identity cached on the cameras row. Optional so legacy
  // rows and existing call sites keep compiling. Field set kept aligned with
  // the shared CameraRef shape (types/deviceRef.ts).
  site_name?: string | null;
  source_id?: string | null;
  agent_name?: string | null;
  nvr_make?: string | null;
  nvr_model?: string | null;
  nvr_firmware?: string | null;
  nvr_serial?: string | null;
  nvr_channels?: number | null;
  channel_no?: number | null;
  camera_make?: string | null;
  camera_model?: string | null;
}

export interface PushTokenResponse {
  camera_id: string;
  push_token: string;
  push_url: string;
  issued_at: string;
  note: string;
}

export interface DirectRegisterInput {
  site_id: string;
  name: string;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  is_ptz?: boolean;
  has_audio?: boolean;
}

// ---------------------------------------------------------------------------
// Sprint 11 — recording stack
// ---------------------------------------------------------------------------
export type RecordingMode = "continuous" | "schedule" | "motion";
export type RecordingStatus = "recording" | "completed" | "failed" | "deleted";
export type NvrClipStatus = "pending" | "downloading" | "ready" | "failed";

export interface RecordingConfig {
  id: string;
  camera_id: string;
  enabled: boolean;
  mode: RecordingMode;
  retention_minutes: number;
  schedule_start?: string | null;
  schedule_end?: string | null;
  created_at: string;
}

export interface RecordingConfigUpsert {
  enabled: boolean;
  mode: RecordingMode;
  retention_minutes: number;
  schedule_start?: string | null;
  schedule_end?: string | null;
}

export interface Recording {
  id: string;
  camera_id: string;
  site_id: string;
  started_at: string;
  ended_at?: string | null;
  duration_sec?: number | null;
  s3_key: string;
  s3_bucket: string;
  size_bytes?: number | null;
  status: RecordingStatus;
  retention_minutes: number;
  created_at: string;
}

export interface RecordingList {
  items: Recording[];
  total: number;
}

export interface RecordingConfigList {
  items: RecordingConfig[];
  total: number;
}

export interface PlaybackResponse {
  recording_id: string;
  hls_playlist: string;
  signed_url_ttl_seconds: number;
  expires_at: string;
}

export interface NvrClip {
  id: string;
  camera_id: string;
  site_id: string;
  clip_source: "nvr";
  nvr_start_time: string;
  nvr_end_time: string;
  duration_sec: number;
  s3_key?: string | null;
  status: NvrClipStatus;
  requested_by: string;
  error_message?: string | null;
  created_at: string;
  signed_url?: string | null;
}

export interface NvrClipList {
  items: NvrClip[];
  total: number;
}

export interface NvrClipRequest {
  camera_id: string;
  start_time: string;
  end_time: string;
}

export const RETENTION_OPTIONS: Array<{ label: string; value: number }> = [
  { label: "10 minutes", value: 10 },
  { label: "1 hour", value: 60 },
  { label: "1 day", value: 1440 },
  { label: "7 days", value: 10080 },
  { label: "30 days", value: 43200 },
  { label: "90 days", value: 129600 },
];

export interface StatusFeedEvent {
  event:
    | "ready"
    | "status_change"
    | "motion_event"
    | "new_alert"
    | "alert_updated";
  camera_id?: string;
  status?: CameraStatus;
  ts: string;
  latency_ms?: number;
  // For alert envelopes
  alert_id?: string;
  rule_type?: string;
  severity?: "info" | "warning" | "critical";
  title?: string;
  site_id?: string;
}

// ---------------------------------------------------------------------------
// Sprint 5 — sites, dashboard, audit, user invites with site assignments
// ---------------------------------------------------------------------------
export interface CameraCounts {
  total: number;
  online: number;
  offline: number;
  degraded: number;
  pending: number;
}

export type SiteProtocol = "onvif" | "isapi" | "dahua" | "rtsp";

export interface Site {
  id: string;
  tenant_id: string;
  name: string;
  code?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
  timezone: string;
  latitude?: number | null;
  longitude?: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  camera_counts?: CameraCounts | null;
  health_pct?: number | null;
  // Sprint 10 fix — NVR identity surfaced for the Sites admin form and
  // the Equipment Health page.  ``nvr_configured`` is computed
  // server-side ("we have host+user"); the password is never returned.
  nvr_host?: string | null;
  nvr_port?: number | null;
  nvr_user?: string | null;
  nvr_configured?: boolean;
  nvr_make?: string | null;
  nvr_model?: string | null;
  nvr_firmware?: string | null;
  nvr_serial?: string | null;
  nvr_channels?: number | null;
  // S11 P4 — vendor-agnostic protocol selector. Defaults to 'isapi' on
  // any row that predated migration 0011.
  protocol: SiteProtocol;
}

export interface ConnectionTestResponse {
  ok: boolean;
  protocol: SiteProtocol;
  message?: string | null;
  latency_ms?: number | null;
  raw?: Record<string, unknown>;
}

export interface SiteList {
  items: Site[];
  total: number;
  limit: number;
  offset: number;
}

export interface SiteCreateInput {
  name: string;
  code?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  timezone?: string;
}

export interface SiteHealth {
  site_id: string;
  total_cameras: number;
  online: number;
  offline: number;
  degraded: number;
  health_pct: number;
}

export interface SiteSummary {
  id: string;
  name: string;
  city?: string | null;
  health_pct: number;
  camera_counts: CameraCounts;
}

export interface DashboardAlert {
  id: string;
  created_at: string;
  action: string;
  actor_email?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
}

// S12 — fielded blocks for the enhanced dashboard.
export interface CamerasBlock {
  total: number;
  online: number;
  offline: number;
  unknown: number;
  recording: number;
}

export interface HealthBlock {
  overall_uptime_pct: number;
  overall_recording_pct: number;
}

export interface AlertsBlock {
  open: number;
  critical: number;
  high: number;
  acknowledged: number;
}

export interface ComplianceBlock {
  // §67 — NULL means "no score to report", which is NOT the same as 0%. The
  // two counts say why: nothing scored yet, or nothing configured to record.
  avg_score_today: number | null;
  cameras_below_threshold: number;
  cameras_scored?: number;
  cameras_unconfigured?: number;
}

export interface RecordingsBlock {
  total_size_gb: number;
  gaps_today: number;
  retention_days: number;
}

export interface FormatTs {
  utc: string;
  local: string;
  timezone: string;
}

export interface RecentAlertEvent {
  id: string;
  title: string;
  camera_id?: string | null;
  severity: string;
  status: string;
  created_at?: FormatTs | null;
}

export interface RecentActivity {
  id: string;
  action: string;
  actor_email?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  created_at?: FormatTs | null;
}

export interface DashboardSummary {
  total_sites: number;
  sites_healthy: number;
  sites_degraded: number;
  sites_offline: number;
  total_cameras: number;
  cameras_online: number;
  cameras_offline: number;
  cameras_pending: number;
  sites: SiteSummary[];
  recent_alerts: DashboardAlert[];
  // S12 additive.
  cameras?: CamerasBlock | null;
  health?: HealthBlock | null;
  alerts?: AlertsBlock | null;
  compliance?: ComplianceBlock | null;
  recordings?: RecordingsBlock | null;
  recent_alert_events?: RecentAlertEvent[];
  recent_activity?: RecentActivity[];
}

export interface AuditEntry {
  id: string;
  created_at: string;
  action: string;
  resource_type?: string | null;
  resource_id?: string | null;
  user_id?: string | null;
  actor_email?: string | null;
  ip_address?: string | null;
  details: Record<string, unknown>;
}

export interface AuditList {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserDetail {
  id: string;
  tenant_id: string;
  email: string;
  full_name?: string | null;
  role: Role;
  is_active: boolean;
  is_mfa_enabled: boolean;
  must_change_password: boolean;
  last_login_at?: string | null;
  created_at: string;
  site_ids?: string[] | null;
}

export interface UserList {
  items: UserDetail[];
  total: number;
  limit: number;
  offset: number;
}

export interface UserInviteInput {
  email: string;
  full_name?: string;
  role: Role;
  password: string;
  must_change_password?: boolean;
  site_ids?: string[];
}

export interface CameraList {
  items: Camera[];
  total: number;
  limit: number;
  offset: number;
}

export interface CameraStatusResponse {
  camera_id: string;
  status: CameraStatus;
  latency_ms: number | null;
  checked_at: string;
  source: "heartbeat" | "live_ping" | "db_only";
}

export interface CameraCreateInput {
  site_id: string;
  agent_id?: string | null;
  connection_mode?: ConnectionMode;
  name: string;
  manufacturer?: string | null;
  model?: string | null;
  onvif_host?: string | null;
  onvif_port?: number | null;
  onvif_username?: string | null;
  onvif_password?: string | null;
  rtsp_main_url?: string | null;
  rtsp_username?: string | null;
  rtsp_password?: string | null;
  is_ptz?: boolean;
  has_audio?: boolean;
}

export interface DiscoveredCamera {
  onvif_host: string;
  onvif_port: number;
  manufacturer?: string | null;
  model?: string | null;
  firmware?: string | null;
  serial_number?: string | null;
  hardware_id?: string | null;
  profile_tokens: string[];
  stream_uri?: string | null;
  discovered_at: string;
}

export interface DiscoveryResults {
  task_id: string;
  status: "pending" | "in_progress" | "completed" | "failed" | "expired";
  cameras: DiscoveredCamera[];
  error?: string | null;
  completed_at?: string | null;
}

export interface DiscoveryResponse {
  task_id: string;
  message: string;
}
